import { createContext, useContext } from 'react';

/**
 * Where a floating panel is rendered in the DOM.
 *
 * The whole of `documentation/architecture/dialog-dropdown-portal-architecture.md`
 * comes down to this prop being present and forwarded. A menu portalled to
 * `document.body` from inside a dialog renders behind the dialog overlay; a menu not
 * portalled at all gets clipped by whatever scrolls. Neither is visible until
 * someone opens that one form in that one context.
 */
export type PopoverPortalTarget = 'body' | 'dialog' | 'inline';

/** Set by `DialogContent` to its own element, so pickers inside it can portal there. */
export const DialogPortalContext = createContext<HTMLElement | null>(null);

/**
 * Resolves a target to a container element.
 *
 * `undefined` means "portal to the default body target"; `null` means "do not
 * portal", which is what `inline` needs.
 */
export const usePopoverContainer = (
  target: PopoverPortalTarget = 'body',
): { portal: boolean; container: HTMLElement | undefined } => {
  const dialogElement = useContext(DialogPortalContext);

  if (target === 'inline') return { portal: false, container: undefined };

  if (target === 'dialog') {
    if (dialogElement) return { portal: true, container: dialogElement };

    // Falling back rather than breaking: a picker that requests the dialog target on
    // a plain page still works. It is a mistake worth surfacing, but not one worth a
    // blank screen for a church treasurer.
    if (import.meta.env.DEV) {
      console.warn(
        '[popoverPortal] "dialog" requested with no Dialog above this component; falling back to body.',
      );
    }
    return { portal: true, container: undefined };
  }

  return { portal: true, container: undefined };
};
